import { Grid, ListItemDecorator, MenuItem, Stack, Typography } from '@mui/joy';
import type { ReactElement } from 'react';
import {
  type IPeriodUnit,
  PortfolioRebalancingRulesDocument,
  RebalancingRuleTemplatesDocument,
  useDeleteRebalancingRuleMutation,
} from '../../../../generated/graphql.tsx';
import { useNavigate } from 'react-router';
import SeeMoreDropdown from '../../../technical/SeeMoreDropDown/SeeMoreDropdown.tsx';
import { DeleteMenuItem } from '../../../technical/inputs/GButton/DeleteDialogButton.tsx';
import { DateTimeFormat, formatDate } from '../../../formatter.utils.ts';
import capitalize from 'lodash/fp/capitalize';
import isNil from 'lodash/fp/isNil';
import GridCard from '../../../technical/GCard/GridCard.tsx';
import { ADD_NEW_CARD_STYLE } from 'components/technical/GCard/consts.ts';
import Edit from '@mui/icons-material/Edit';

const cardHeight = '10rem';

const RebalancingRuleList = ({
  createNewRuleUrl,
  rebalancingRules,
  editUrl,
}: {
  createNewRuleUrl: string;
  rebalancingRules: {
    id: string;
    name: string;
    periodUnit?: IPeriodUnit | null | undefined;
    periodValue?: number | null | undefined;
    start: UtcDate;
    tradingFee: Decimal;
  }[];
  editUrl?: (id: string) => string;
}): ReactElement => {
  const gridWidth = { sm: 12, md: 6, lg: 3 };
  const navigate = useNavigate();

  const [deleteRebalancingRule] = useDeleteRebalancingRuleMutation();

  const formatFeeValue = (value: Decimal): string => {
    return `${Number.parseFloat(value) * 100}%`;
  };

  return (
    <Grid container>
      <GridCard
        cardSx={ADD_NEW_CARD_STYLE}
        onClick={() => {
          navigate(createNewRuleUrl);
        }}
        height={cardHeight}
        gridWidth={gridWidth}
        addNewText="Add new"
      />
      {rebalancingRules.map((rule) => (
        <GridCard key={rule.id} height={cardHeight} gridWidth={gridWidth}>
          <Stack direction="column" justifyContent="space-between" height="100%">
            <Stack justifyContent="space-between" direction="row">
              <Typography level="title-lg" component="h3">
                {rule.name}
              </Typography>
              <SeeMoreDropdown>
                {editUrl && (
                  <MenuItem onClick={() => navigate(editUrl(rule.id))}>
                    <ListItemDecorator>
                      <Edit />
                    </ListItemDecorator>
                    Edit
                  </MenuItem>
                )}
                <DeleteMenuItem
                  deletedMessage="Rule successfully deleted"
                  confirmationMessage={<>Are you sure you want to remove rule {rule.name}?</>}
                  deleteItem={(): Promise<unknown> => {
                    return deleteRebalancingRule({
                      variables: {
                        id: rule.id,
                      },
                      refetchQueries: [RebalancingRuleTemplatesDocument, PortfolioRebalancingRulesDocument],
                    });
                  }}
                />
              </SeeMoreDropdown>
            </Stack>
            <Typography level="body-sm" component="div">
              {!isNil(rule.periodUnit) && <div>Period: {capitalize(rule.periodUnit)}</div>}
              {!isNil(rule.periodValue) && <div>Interval: {rule.periodValue}</div>}
              <div>Starting at: {formatDate(rule.start, DateTimeFormat.Date)}</div>
              <div>Trading fee: {formatFeeValue(rule.tradingFee)}</div>
            </Typography>
          </Stack>
        </GridCard>
      ))}
    </Grid>
  );
};

export default RebalancingRuleList;
